// P0 attribution hardening (Feb 7, 2026) — centralized seed storage.
//
// Why this exists: every page that reads/writes the referral seed used to
// poke at sessionStorage directly, which broke in three real-world cases:
//   1. Two-tab race (sessionStorage is per-tab, so seeding in Tab B was
//      invisible to a /signup form open in Tab A).
//   2. Browser restart / private mode (sessionStorage is wiped on close).
//   3. "Click on phone, type /signup later on laptop" (no transfer at all,
//      but at least localStorage survives within the same browser).
// localStorage solves cases 1 and 2 cleanly. Case 3 is fixed server-side
// via the visitor_sig cookie fallback in /auth/signup.
//
// We keep the same key names so any existing seed in a user's session is
// honored on first read (one-time migration: copy sessionStorage → localStorage
// on first import so users mid-flow don't lose their seed).

const REF_KEY = "mysilo_ref_sig";
const FROM_SILO_KEY = "mysilo_from_silo";

// Sep 27, 2026 (Randy) — the silo-visit memory EXPIRES; the referral seed does
// not. His question: "if a user happened to have been on My Silo a few days
// prior looking at a random person's public QA, their computer saves the seed
// and when they sign up from the vanilla website it assigns them to that public
// QA owner?" Yes — and it used to be forever, which is why /signup still
// announced a silo he had visited weeks earlier. Seven days is his call.
// A real ?ref= link is unaffected: it is a different key, and the server
// resolves `referrer_sig` BEFORE it ever looks at `from_silo_slug`.
const FROM_SILO_TTL_MS = 7 * 24 * 60 * 60 * 1000;
// Values written before this shipped carry no timestamp and could be months
// old. Rather than drop them (someone may be mid-decision right now) or trust
// them (the whole bug), they are stamped as already 6 days old: a fan who
// visited today keeps ~1 day of grace, and every stale one clears itself.
const FROM_SILO_LEGACY_AGE_MS = 6 * 24 * 60 * 60 * 1000;

// One-time migration: copy any pre-existing sessionStorage values into
// localStorage so a user mid-share-flow doesn't lose attribution when this
// ships. Idempotent — only runs if local is empty.
try {
  if (typeof window !== "undefined") {
    const ssRef = sessionStorage.getItem(REF_KEY);
    const ssFromSilo = sessionStorage.getItem(FROM_SILO_KEY);
    if (ssRef && !localStorage.getItem(REF_KEY)) localStorage.setItem(REF_KEY, ssRef);
    if (ssFromSilo && !localStorage.getItem(FROM_SILO_KEY)) localStorage.setItem(FROM_SILO_KEY, ssFromSilo);
  }
} catch { /* private mode / disabled storage — proceed without */ }

const safeGet = (k) => {
  try { return localStorage.getItem(k); } catch { return null; }
};
const safeSet = (k, v) => {
  try { localStorage.setItem(k, v); } catch { /* storage disabled */ }
};
const safeRemove = (k) => {
  try { localStorage.removeItem(k); } catch { /* */ }
};

export const getRefSig = () => safeGet(REF_KEY) || "";
export const setRefSig = (v) => safeSet(REF_KEY, v);
export const clearRefSig = () => safeRemove(REF_KEY);

export const getFromSilo = () => {
  const raw = safeGet(FROM_SILO_KEY);
  if (!raw) return "";
  let slug = "";
  let stamp = 0;
  if (raw.charAt(0) === "{") {
    try {
      const parsed = JSON.parse(raw);
      slug = parsed.v || "";
      stamp = Number(parsed.t) || 0;
    } catch { safeRemove(FROM_SILO_KEY); return ""; }
  } else {
    slug = raw;
    stamp = Date.now() - FROM_SILO_LEGACY_AGE_MS;
    safeSet(FROM_SILO_KEY, JSON.stringify({ v: slug, t: stamp }));
  }
  if (!slug) return "";
  if (Date.now() - stamp > FROM_SILO_TTL_MS) { safeRemove(FROM_SILO_KEY); return ""; }
  return slug;
};
export const setFromSilo = (v) => safeSet(FROM_SILO_KEY, JSON.stringify({ v, t: Date.now() }));
export const clearFromSilo = () => safeRemove(FROM_SILO_KEY);

// Oct 2026 (Randy, seeding pass) — the VISIT RING.
//
// `from_silo` remembers one slug and overwrites it, which is why a fan who
// visited their buddy's silo and then browsed three more out of curiosity had
// the credit quietly handed to the third one. The ring remembers the last few
// so signup can ASK instead of guess.
//
// Deliberately device-local. A server-side history keyed on the visitor_sig
// cookie would show Person A's browsing to Person B on a shared machine — the
// picker is a disclosure surface, so it stays on the device that did the
// browsing. Same 7-day TTL as the single slot it supplements.
const VISITED_KEY = "mysilo_visited_silos";
const VISITED_MAX = 5;

export const getVisitedSilos = () => {
  const raw = safeGet(VISITED_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const fresh = parsed.filter(
      (e) => e && e.v && Date.now() - (Number(e.t) || 0) <= FROM_SILO_TTL_MS
    );
    if (fresh.length !== parsed.length) safeSet(VISITED_KEY, JSON.stringify(fresh));
    return fresh.map((e) => e.v);
  } catch { safeRemove(VISITED_KEY); return []; }
};

export const pushVisitedSilo = (slug) => {
  if (!slug) return;
  const raw = safeGet(VISITED_KEY);
  let list = [];
  try { list = raw ? JSON.parse(raw) : []; } catch { list = []; }
  if (!Array.isArray(list)) list = [];
  // Most recent first, no duplicates — revisiting a silo moves it to the top,
  // which is what makes "go brush your friend's page and come back" work.
  list = list.filter((e) => e && e.v && e.v !== slug);
  list.unshift({ v: slug, t: Date.now() });
  safeSet(VISITED_KEY, JSON.stringify(list.slice(0, VISITED_MAX)));
};

export const clearVisitedSilos = () => safeRemove(VISITED_KEY);

// Read the long-lived visitor_sig cookie (400-day persistence). Used by the
// signup form to forward it to the backend as a fallback attribution lookup
// when localStorage has been wiped.
export const getVisitorSig = () => {
  try {
    const m = document.cookie.split("; ").find((c) => c.startsWith("mysilo_visitor_sig="));
    return m ? m.split("=")[1] : "";
  } catch { return ""; }
};
